import { Check, ChevronRight, HelpCircle, Inbox, ListChecks, type LucideIcon, OctagonAlert, RefreshCw, RotateCcw, Scale, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFixedParam } from '@/lib/scopedParams';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import i18n from '@/i18n';
import type { registry as registryEn } from '@/i18n/locales/en/registry';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import type { ConnectionSettingsView, PollMessage, ReconciliationIssue, RegistryCapabilities, RegistrySummary, RegistryTransactionRow } from '../../api/areas/registry';
import { Badge, type Tone, TONE_CLASS } from '../../components/Badge';
import { Callout, DataTable, Field, type Column } from '../../components/blocks';
import { EntityRef } from '../../components/EntityRef';
import { CorrelationLink, DomainLink } from '../../components/Ids';
import type { Crumb } from '../../components/PageHeader';
import { formatCount, formatDate, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { useLabels } from '../../lib/labels';
import { COMMAND_STATE, humanize, type Meta } from '../domains/status';

export const REGISTRY_STATUS: Record<string, Meta> = {
  ACTIVE: { label: 'Active', tone: 'ok' },
  SUSPENDED: { label: 'Suspended', tone: 'warn' },
  RETIRED: { label: 'Retired', tone: 'neutral' },
};

export const CREDENTIAL_STATUS: Record<string, Meta> = {
  PENDING: { label: 'Pending', tone: 'pending' },
  ACTIVE: { label: 'Active', tone: 'ok' },
  ROTATED: { label: 'Rotated', tone: 'neutral' },
  REVOKED: { label: 'Revoked', tone: 'crit' },
};

export const ISSUE_STATE: Record<string, Meta> = {
  OPEN: { label: 'Open', tone: 'warn' },
  AUTO_RECONCILING: { label: 'Auto-reconciling', tone: 'pending' },
  MANUAL_REVIEW: { label: 'Manual review', tone: 'crit' },
  RESOLVED: { label: 'Resolved', tone: 'ok' },
  IGNORED: { label: 'Ignored', tone: 'neutral' },
};

export const SEVERITY: Record<string, Meta> = {
  LOW: { label: 'Low', tone: 'info' },
  MEDIUM: { label: 'Medium', tone: 'warn' },
  HIGH: { label: 'High', tone: 'serious' },
  CRITICAL: { label: 'Critical', tone: 'crit' },
};

/** Command states whose registry outcome is not known yet (resolved by reconciliation). */
export const UNRESOLVED_STATES = new Set(['UNKNOWN', 'RECONCILING', 'MANUAL_REVIEW']);

export type RegistryEnumGroup = keyof (typeof registryEn)['enum'];

type LooseT = (key: string, options?: Record<string, unknown>) => string;

/** Translated label for a backend enum value; unknown values fall back to the humanised code. */
export function enumLabel(group: RegistryEnumGroup, value: string): string {
  const key = `registry:enum.${group}.${value}`;
  return i18n.exists(key) ? (i18n.t as unknown as LooseT)(key) : humanize(value);
}

/** Breadcrumb for every registry page, in the active language; detail pages append their own. */
export function useRegistryCrumbs(...extra: Crumb[]): Crumb[] {
  const { t } = useTranslation('registry');
  return [{ label: t('area') }, ...extra];
}

/** Status badge with a translated label and the raw code in `title`. */
export function EnumBadge({ group, map, value }: { group: RegistryEnumGroup; map: Readonly<Record<string, Meta>>; value: string | null | undefined }) {
  useTranslation('registry');
  if (!value) return <Badge tone="neutral">—</Badge>;
  const meta = map[value] ?? map[value.toUpperCase()];
  return (
    <Badge tone={meta?.tone ?? 'neutral'} title={value}>
      {enumLabel(group, value)}
    </Badge>
  );
}

/** Registry command outcome. UNKNOWN is emphasised: it is never retried blindly, reconciliation decides. */
export function OutcomeBadge({ value }: { value: string | null | undefined }) {
  useTranslation('registry');
  if (!value) return <Badge tone="neutral">—</Badge>;
  if (value === 'UNKNOWN') {
    return (
      <span
        title={value}
        className={cn('inline-flex w-fit shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold ring-1 ring-serious/40', TONE_CLASS.serious)}
      >
        <HelpCircle className="size-3" aria-hidden="true" />
        {enumLabel('commandState', value)}
      </span>
    );
  }
  const meta = COMMAND_STATE[value as keyof typeof COMMAND_STATE];
  return (
    <Badge tone={meta?.tone ?? 'neutral'} title={value}>
      {enumLabel('commandState', value)}
    </Badge>
  );
}

/** The "never blindly retried" rule for UNKNOWN outcomes. */
export function UnknownOutcomeCallout({ tone = 'serious', link = true, className }: { tone?: 'serious' | 'info' | 'warn'; link?: boolean; className?: string }) {
  const { t } = useTranslation('registry');
  return (
    <Callout tone={tone} title={t('unknown.title')} className={className}>
      <p>{t('unknown.body')}</p>
      {link ? (
        <Link to="/registry/reconciliation" className="mt-1 inline-flex items-center gap-1 font-medium text-primary hover:underline">
          {t('unknown.link')} <ChevronRight className="size-3.5" aria-hidden="true" />
        </Link>
      ) : null}
    </Callout>
  );
}

/** Sets or clears one URL search parameter, dropping the paging cursor; `reset` clears every filter. */
export function useParamSetter(): [URLSearchParams, (k: string, v: string) => void, () => void] {
  const [params, setParams] = useSearchParams();
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    if (k !== 'before') p.delete('before');
    if (k !== 'after') p.delete('after');
    setParams(p);
  };
  const reset = () => setParams(new URLSearchParams());
  return [params, set, reset];
}

const ANY = '__any__';

export interface Option {
  value: string;
  label: string;
}

/** A labelled select for a FilterBar; the empty value means "any". */
export function FilterSelect({
  label,
  value,
  onChange,
  options,
  anyLabel,
  className,
}: {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  anyLabel?: string;
  className?: string;
}) {
  const { t } = useTranslation('registry');
  return (
    <Field label={label} className={cn('min-w-36', className)}>
      <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? '' : v)}>
        <SelectTrigger size="sm" className="w-full min-w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{anyLabel ?? t('any')}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} title={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/** A labelled text filter applied on Enter or blur. */
export function TextFilter({
  label,
  value,
  placeholder,
  onApply,
  className,
}: {
  label: ReactNode;
  value: string;
  placeholder?: string;
  onApply: (value: string) => void;
  className?: string;
}) {
  const { t } = useTranslation('registry');
  return (
    <Field label={label} className={cn('min-w-40', className)}>
      <Input
        key={value}
        type="search"
        className="h-8"
        defaultValue={value}
        placeholder={placeholder}
        title={t('filter.enterHint')}
        spellCheck={false}
        onKeyDown={(e) => e.key === 'Enter' && onApply(e.currentTarget.value.trim())}
        onBlur={(e) => e.target.value.trim() !== value && onApply(e.target.value.trim())}
      />
    </Field>
  );
}

export function ResetButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  const { t } = useTranslation('registry');
  return (
    <Button type="button" variant="ghost" size="sm" onClick={onClick} disabled={disabled}>
      <RotateCcw /> {t('resetFilters')}
    </Button>
  );
}

/** Registry filter: the operator picks a registry by name, the URL carries its code. */
export function RegistrySelect({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  const { t } = useTranslation('registry');
  // On a registry's own page the registry is fixed: no filter to show.
  const fixed = useFixedParam('registry');
  const regs = useApi((signal) => api.listRegistries({ signal }), []);
  if (fixed) return null;
  const options = regs.status === 'success' ? regs.data.items.map((r) => ({ value: r.code, label: r.name })) : [];
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: value });
  return <FilterSelect label={t('filter.registry')} value={value} onChange={onChange} options={options} />;
}

export function RefreshButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('registry');
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick}>
      <RefreshCw /> {t('refresh')}
    </Button>
  );
}

/** Keyset pager footer: back to the newest page, or on to older rows. */
export function Pager({ next, onNext, first, onFirst }: { next: string | null; onNext: (cursor: string) => void; first: boolean; onFirst: () => void }) {
  const { t } = useTranslation('registry');
  if (!next && first) return null;
  return (
    <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
      {!first ? (
        <Button type="button" variant="ghost" size="sm" onClick={onFirst}>
          {t('newest')}
        </Button>
      ) : null}
      {next ? (
        <Button type="button" variant="outline" size="sm" onClick={() => onNext(next)}>
          {t('older')} <ChevronRight />
        </Button>
      ) : null}
    </div>
  );
}

/** A duration in milliseconds, in the largest whole unit. */
export function ms(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const t = i18n.t as unknown as LooseT;
  if (value >= 60_000 && value % 60_000 === 0) return t('registry:units.min', { n: value / 60_000 });
  if (value >= 1000 && value % 1000 === 0) return t('registry:units.s', { n: value / 1000 });
  return t('registry:units.ms', { n: value });
}

export function When({ at }: { at: string | null | undefined }) {
  if (!at) return <>—</>;
  return (
    <time dateTime={at} title={formatDateTime(at)} className="whitespace-nowrap">
      {formatRelative(at)}
    </time>
  );
}

/** Small uppercase heading used inside cards. */
export function SubHeading({ children }: { children: ReactNode }) {
  return <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{children}</h3>;
}

/** A count that is quiet at zero and a toned badge otherwise. */
export function CountBadge({ n, tone }: { n: number; tone: 'warn' | 'crit' | 'serious' | 'info' }) {
  return n ? (
    <Badge tone={tone} className="tabular">
      {formatCount(n)}
    </Badge>
  ) : (
    <span className="text-muted-foreground">0</span>
  );
}

/** Traffic-class allocation of one connection pool. */
export function ClassTable({ settings }: { settings: ConnectionSettingsView }) {
  const { t } = useTranslation('registry');
  const classes = Object.entries(settings.classes);
  if (classes.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('classTable.empty')}</p>;
  }
  const columns: Column<(typeof classes)[number]>[] = [
    { key: 'name', header: t('classTable.class'), cell: ([name]) => <span title={name}>{enumLabel('trafficClass', name)}</span> },
    { key: 'sessions', header: t('classTable.sessions'), numeric: true, cell: ([, c]) => c.sessions },
    { key: 'rps', header: t('classTable.rps'), numeric: true, cell: ([, c]) => c.requests_per_second },
    { key: 'burst', header: t('classTable.burst'), numeric: true, cell: ([, c]) => c.burst },
  ];
  return (
    <div className="overflow-hidden rounded-lg border">
      <DataTable columns={columns} rows={classes} rowKey={([name]) => name} />
    </div>
  );
}

export function Flag({ on, yes, no }: { on: boolean; yes?: ReactNode; no?: ReactNode }) {
  const { t } = useTranslation('registry');
  return on ? <Badge tone="ok">{yes ?? t('yes')}</Badge> : <Badge tone="neutral">{no ?? t('no')}</Badge>;
}

/** A registry by name (its code underneath), linking to the registry page. */
export function RegistryRef({ name, code, className }: { name: string; code: string; className?: string }) {
  return <EntityRef label={name} sub={code} to={`/registry/registries/${encodeURIComponent(code)}`} className={className} />;
}

/** A zone by name, linking to the zone page. */
export function TldRef({ zone, id, sub }: { zone: string; id: string; sub?: ReactNode }) {
  return <EntityRef label={`.${zone}`} sub={sub} to={`/registry/tlds/${encodeURIComponent(id)}`} id={id} />;
}

/** Chevron cell that opens a record's page. */
export function OpenCell({ to, label }: { to: string; label: string }) {
  return (
    <Button asChild variant="ghost" size="icon-xs">
      <Link to={to} aria-label={label}>
        <ChevronRight />
      </Link>
    </Button>
  );
}

/** A stored value or payload, collapsed, already redacted by the API. */
export function PayloadBlock({ label, value }: { label: string; value: unknown }) {
  const { t } = useTranslation('registry');
  if (value === undefined) return <p className="text-xs text-muted-foreground">{t('reconciliation.withheld', { label })}</p>;
  if (value === null) return <p className="text-xs text-muted-foreground">{t('reconciliation.noValue', { label })}</p>;
  return (
    <details className="group text-xs" open>
      <summary className="flex cursor-pointer list-none items-center gap-1 font-medium text-foreground/80 hover:text-foreground">
        <ChevronRight className="size-3 transition-transform group-open:rotate-90" aria-hidden="true" />
        {label}
      </summary>
      <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-muted p-2 font-mono text-[11px] leading-snug">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Per-registry health counters, shared by the registry list and the registry page

interface Health {
  key: 'openCommands' | 'unknown' | 'drift' | 'poll';
  icon: LucideIcon;
  tone: Tone;
  count: (r: RegistrySummary) => number;
  to: (reg: string) => string;
}

export const HEALTH: Health[] = [
  { key: 'openCommands', icon: ListChecks, tone: 'warn', count: (r) => r.open_transactions, to: (reg) => `/registry/transactions${reg ? `?registry=${reg}` : ''}` },
  {
    key: 'unknown',
    icon: OctagonAlert,
    tone: 'serious',
    count: (r) => r.unresolved_transactions,
    to: (reg) => `/registry/transactions?${reg ? `registry=${reg}&` : ''}state=UNRESOLVED`,
  },
  { key: 'drift', icon: Scale, tone: 'crit', count: (r) => r.open_reconciliation_issues, to: (reg) => `/registry/reconciliation${reg ? `?registry=${reg}` : ''}` },
  { key: 'poll', icon: Inbox, tone: 'warn', count: (r) => r.unprocessed_poll_messages, to: (reg) => `/registry/poll?${reg ? `registry=${reg}&` : ''}status=UNPROCESSED` },
];

const TILE_TONE: Record<string, string> = {
  warn: 'border-warning/40 bg-warning-soft/60',
  serious: 'border-serious/40 bg-serious-soft/60',
  crit: 'border-critical/40 bg-critical-soft/60',
};

/** The four queue counters of one registry, each linking to the queue it counts. */
export function HealthTiles({ r }: { r: RegistrySummary }) {
  const { t } = useTranslation('registry');
  const reg = encodeURIComponent(r.code);
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {HEALTH.map((h) => {
        const n = h.count(r);
        const Icon = h.icon;
        return (
          <Link
            key={h.key}
            to={h.to(reg)}
            className={cn(
              'group flex flex-col gap-1 rounded-lg border p-3 transition-colors hover:border-primary/50 hover:bg-accent/40 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
              n > 0 ? TILE_TONE[h.tone] : 'bg-muted/30',
            )}
          >
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Icon className="size-3.5" aria-hidden="true" />
              {t(`registries.${h.key}`)}
            </span>
            <span className={cn('tabular text-2xl font-semibold', n === 0 && 'text-muted-foreground')}>{formatCount(n)}</span>
            <span className="text-xs text-muted-foreground">{t(`registries.${h.key}Hint`)}</span>
          </Link>
        );
      })}
    </div>
  );
}

const CAPABILITY_FLAGS: (keyof RegistryCapabilities & `${'supports' | 'requires'}_${string}`)[] = [
  'supports_epp',
  'supports_contact_objects',
  'supports_host_objects',
  'supports_dnssec',
  'supports_rgp',
  'supports_fee_extension',
  'supports_change_poll',
  'supports_idn',
  'supports_premium',
  'supports_transfer',
  'supports_restore',
  'requires_local_presence',
  'requires_documents',
];

/** What the registry lets the registrar do, as the capability version in force states it. */
export function Capabilities({ c }: { c: RegistryCapabilities | null }) {
  const { t } = useTranslation('registry');
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <SubHeading>{t('registries.capabilities')}</SubHeading>
        {c ? <span className="text-xs text-muted-foreground">{t('registries.capabilitiesVersion', { version: c.version, date: formatDate(c.effective_from) })}</span> : null}
      </div>
      {c ? (
        <>
          <ul className="flex flex-wrap gap-1.5">
            {CAPABILITY_FLAGS.map((k) => {
              const on = Boolean(c[k]);
              return (
                <li
                  key={k}
                  className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs', on ? 'border-success/30 bg-success-soft text-success' : 'border-dashed text-muted-foreground')}
                  title={on ? t('registries.supported') : t('registries.notSupported')}
                >
                  {on ? <Check className="size-3" aria-hidden="true" /> : <X className="size-3" aria-hidden="true" />}
                  <span className={cn(!on && 'line-through')}>{t(`registries.cap.${k}`)}</span>
                  <span className="sr-only">: {on ? t('registries.supported') : t('registries.notSupported')}</span>
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-muted-foreground">{t('registries.limits', { reg: c.max_registration_years, renew: c.max_renew_years })}</p>
        </>
      ) : (
        <Callout tone="warn">{t('registries.noCapabilities')}</Callout>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Record tables shared by the list pages and the detail pages

/** Registry transaction rows (no payloads); every row opens the command. */
export function TransactionTable({ rows, showRegistry: show }: { rows: RegistryTransactionRow[]; showRegistry?: boolean }) {
  const { t } = useTranslation('registry');
  const fixedRegistry = useFixedParam('registry');
  const showRegistry = show ?? !fixedRegistry;
  const L = useLabels();
  const columns: Column<RegistryTransactionRow>[] = [
    { key: 'started', header: t('tx.started'), cell: (r) => <When at={r.started_at} /> },
    ...(showRegistry
      ? [{ key: 'registry', header: t('tx.registry'), cell: (r: RegistryTransactionRow) => <RegistryRef name={r.registry_name} code={r.registry_code} /> }]
      : []),
    {
      key: 'command',
      header: t('tx.command'),
      cell: (r) => (
        <div className="space-y-0.5">
          <Link to={`/registry/transactions/${encodeURIComponent(r.id)}`} className="text-sm font-semibold text-primary hover:underline" title={r.operation}>
            {L.eppCommand(r.operation)}
          </Link>
          {r.attempt > 1 ? <div className="text-xs text-muted-foreground">{t('tx.attempt', { n: r.attempt })}</div> : null}
          {r.correlation_id || r.workflow_id ? (
            <div className="flex flex-wrap gap-x-2 text-xs">
              {r.correlation_id ? <CorrelationLink id={r.correlation_id} label={t('tx.corr')} /> : null}
              {/* The workflow is named by what it does, so the row links by name, not by id. */}
              {r.workflow_id ? (
                <Link to={`/ops/workflows/${encodeURIComponent(r.workflow_id)}`} className="text-primary hover:underline">
                  {t('tx.workflow')}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'object',
      header: t('tx.object'),
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="text-xs text-muted-foreground" title={r.object_type}>
            {enumLabel('objectType', r.object_type)}
          </div>
          {r.object_type === 'DOMAIN' && r.object_key ? <DomainLink keyOrName={r.object_key} /> : <code className="text-xs">{r.object_key ?? '—'}</code>}
        </div>
      ),
    },
    { key: 'state', header: t('tx.state'), cell: (r) => <OutcomeBadge value={r.state} /> },
    {
      key: 'result',
      header: t('tx.result'),
      cell: (r) =>
        r.result_code === null && !r.error_class ? (
          '—'
        ) : (
          <div className="space-y-0.5">
            {r.result_code !== null ? (
              <code className="text-xs" title={r.result_message ?? undefined}>
                {r.result_code}
              </code>
            ) : null}
            {r.error_class ? <div className="font-mono text-xs text-muted-foreground">{r.error_class}</div> : null}
          </div>
        ),
    },
    {
      key: 'trids',
      header: t('tx.trids'),
      cell: (r) => (
        <div className="font-mono text-xs">
          <div>{r.cltrid}</div>
          <div className="text-muted-foreground">{r.svtrid ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'class',
      header: t('tx.classLatency'),
      cell: (r) => (
        <div className="space-y-0.5 text-xs">
          <div title={r.traffic_class}>{enumLabel('trafficClass', r.traffic_class)}</div>
          <div className="tabular text-muted-foreground">{r.latency_ms !== null ? ms(r.latency_ms) : '—'}</div>
        </div>
      ),
    },
    {
      key: 'go',
      header: <span className="sr-only">{t('tx.open', { id: '' })}</span>,
      className: 'w-8',
      cell: (r) => <OpenCell to={`/registry/transactions/${encodeURIComponent(r.id)}`} label={t('tx.open', { id: r.cltrid })} />,
    },
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} />;
}

/** Drift and reconciliation issues; every row opens the issue. `compact` drops the registry and resolution. */
export function IssueTable({ rows, compact = false, empty }: { rows: ReconciliationIssue[]; compact?: boolean; empty?: ReactNode }) {
  const { t } = useTranslation('registry');
  const oneRegistry = Boolean(useFixedParam('registry'));
  const L = useLabels();
  const columns: Column<ReconciliationIssue>[] = [
    { key: 'detected', header: t('reconciliation.detected'), cell: (i) => <When at={i.detected_at} /> },
    ...(compact || oneRegistry
      ? []
      : [{ key: 'registry', header: t('reconciliation.registry'), cell: (i: ReconciliationIssue) => <RegistryRef name={i.registry_name} code={i.registry_code} /> }]),
    { key: 'domain', header: t('reconciliation.domain'), cell: (i) => (i.domain_name ? <DomainLink keyOrName={i.domain_name} /> : '—') },
    {
      key: 'type',
      header: t('reconciliation.type'),
      cell: (i) => (
        <span className="text-xs" title={i.issue_type}>
          {enumLabel('issueType', i.issue_type)}
        </span>
      ),
    },
    { key: 'severity', header: t('reconciliation.severity'), cell: (i) => <EnumBadge group="severity" map={SEVERITY} value={i.severity} /> },
    { key: 'state', header: t('reconciliation.state'), cell: (i) => <EnumBadge group="issueState" map={ISSUE_STATE} value={i.state} /> },
    {
      key: 'tx',
      header: t('reconciliation.transaction'),
      cell: (i) =>
        i.registry_transaction_id ? (
          <div className="space-y-1 text-xs">
            <Link
              to={`/registry/transactions/${encodeURIComponent(i.registry_transaction_id)}`}
              className="font-semibold text-primary hover:underline"
              title={i.transaction_operation ?? undefined}
            >
              {i.transaction_operation ? L.eppCommand(i.transaction_operation) : t('reconciliation.command')}
            </Link>
            {i.transaction_state ? <OutcomeBadge value={i.transaction_state} /> : null}
          </div>
        ) : (
          '—'
        ),
    },
    ...(compact
      ? []
      : [
          {
            key: 'resolution',
            header: t('reconciliation.resolution'),
            className: 'min-w-40 whitespace-normal',
            cell: (i: ReconciliationIssue) => (
              <div className="space-y-0.5 text-xs">
                <div>{i.resolution ?? '—'}</div>
                {i.resolved_at ? (
                  <div className="text-muted-foreground">
                    {i.resolved_by ?? t('reconciliation.unknownActor')} · <When at={i.resolved_at} />
                  </div>
                ) : null}
              </div>
            ),
          },
        ]),
    {
      key: 'go',
      header: <span className="sr-only">{t('reconciliation.open')}</span>,
      className: 'w-8',
      cell: (i) => <OpenCell to={`/registry/reconciliation/${encodeURIComponent(i.id)}`} label={t('reconciliation.openOne', { type: enumLabel('issueType', i.issue_type) })} />,
    },
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(i) => i.id} empty={empty} />;
}

/** Poll messages; every row opens the message. `compact` drops the registry column. */
export function PollTable({ rows, compact = false, empty }: { rows: PollMessage[]; compact?: boolean; empty?: ReactNode }) {
  const { t } = useTranslation('registry');
  const oneRegistry = Boolean(useFixedParam('registry'));
  const columns: Column<PollMessage>[] = [
    { key: 'received', header: t('poll.received'), cell: (p) => <When at={p.received_at} /> },
    ...(compact || oneRegistry ? [] : [{ key: 'registry', header: t('poll.registry'), cell: (p: PollMessage) => <RegistryRef name={p.registry_name} code={p.registry_code} /> }]),
    { key: 'id', header: t('poll.messageId'), cell: (p) => <code className="text-xs">{p.registry_message_id}</code> },
    {
      key: 'message',
      header: t('poll.message'),
      className: 'min-w-64 whitespace-normal',
      cell: (p) => (
        <div className="space-y-1">
          <div>{p.message ?? <span className="text-muted-foreground">—</span>}</div>
          {p.processing_error ? <div className="text-xs font-medium text-critical">{p.processing_error}</div> : null}
        </div>
      ),
    },
    {
      key: 'object',
      header: t('poll.object'),
      cell: (p) => (p.domain_name ? <DomainLink keyOrName={p.domain_name} /> : <code className="text-xs">{p.object_name ?? '—'}</code>),
    },
    { key: 'acked', header: t('poll.acked'), cell: (p) => (p.acked_at ? <When at={p.acked_at} /> : <Badge tone="warn">{t('poll.no')}</Badge>) },
    {
      key: 'processed',
      header: t('poll.processed'),
      cell: (p) => (p.processed_at ? <When at={p.processed_at} /> : p.processing_error ? <Badge tone="crit">{t('poll.error')}</Badge> : <Badge tone="pending">{t('poll.pending')}</Badge>),
    },
    {
      key: 'go',
      header: <span className="sr-only">{t('poll.open')}</span>,
      className: 'w-8',
      cell: (p) => <OpenCell to={`/registry/poll/${encodeURIComponent(p.id)}`} label={t('poll.openOne', { id: p.registry_message_id })} />,
    },
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(p) => p.id} empty={empty} />;
}

/** A registry connection's adapter in words ("EPP tiêu chuẩn"), the code in the tooltip. */
export function AdapterLabel({ adapter }: { adapter: string | null | undefined }) {
  const { t } = useTranslation('integrations');
  if (!adapter) return <span className="text-muted-foreground">—</span>;
  return <span title={adapter}>{(t as unknown as (k: string, o: { defaultValue: string }) => string)(`adapter.${adapter}`, { defaultValue: adapter })}</span>;
}
